import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { Eye, Pencil, Trash2 } from 'lucide-react';
import {
    DropdownMenu,
    DropdownItem
} from './style';

interface ActionsDropdownProps {
    buttonRef: HTMLButtonElement | null;
    onView?: () => void;
    onEdit?: () => void;
    onDelete?: () => void;
    onClose?: () => void;
}

const ActionsDropdown: React.FC<ActionsDropdownProps> = ({
    buttonRef,
    onView,
    onEdit,
    onDelete,
    onClose
}) => {
    const [dropdownPosition, setDropdownPosition] = useState<{
        top: number;
        left: number;
        width: number;
        direction: 'up' | 'down'
    }>({ top: 0, left: 0, width: 0, direction: 'down' });

    const dropdownRef = useRef<HTMLDivElement>(null);

    const updatePosition = useCallback(() => {
        if (!buttonRef) return;

        const buttonRect = buttonRef.getBoundingClientRect();
        const menuHeight = 100;
        const menuWidth = 200;
        const windowHeight = window.innerHeight;

        const buttonCenterX = buttonRect.left + (buttonRect.width / 2);
        const left = buttonCenterX - (menuWidth / 2);

        const spaceBelow = windowHeight - buttonRect.bottom;
        const spaceAbove = buttonRect.top;

        const direction: 'up' | 'down' = spaceBelow < menuHeight && spaceAbove > spaceBelow ? 'up' : 'down';
        const top = direction === 'up'
            ? buttonRect.top - menuHeight - 4
            : buttonRect.bottom + 4;

        setDropdownPosition({
            top: Math.round(top),
            left: Math.round(left),
            width: menuWidth,
            direction
        });
    }, [buttonRef]);

    // Posição inicial
    useEffect(() => {
        updatePosition();
    }, [updatePosition]);

    // Click fora
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(target) &&
                buttonRef &&
                !buttonRef.contains(target)
            ) {
                onClose?.();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [onClose, buttonRef]);

    // Scroll e resize — fecha o dropdown
    useEffect(() => {
        const handleScrollOrResize = () => onClose?.();

        window.addEventListener('scroll', handleScrollOrResize, true);
        window.addEventListener('resize', handleScrollOrResize);

        return () => {
            window.removeEventListener('scroll', handleScrollOrResize, true);
            window.removeEventListener('resize', handleScrollOrResize);
        };
    }, [onClose]);

    const handleAction = (action: () => void) => {
        action();
        onClose?.();
    };

    return ReactDOM.createPortal(
        <DropdownMenu
            ref={dropdownRef}
            top={dropdownPosition.top}
            left={dropdownPosition.left}
            width={dropdownPosition.width}
            direction={dropdownPosition.direction}
        >
            {onView && (
                <DropdownItem onClick={() => handleAction(onView)}>
                    <Eye size={16} />
                    Visualizar
                </DropdownItem>
            )}
            {onEdit && (
                <DropdownItem onClick={() => handleAction(onEdit)}>
                    <Pencil size={16} />
                    Editar
                </DropdownItem>
            )}
            {onDelete && (
                <DropdownItem onClick={() => handleAction(onDelete)}>
                    <Trash2 size={16} />
                    Excluir
                </DropdownItem>
            )}
        </DropdownMenu>,
        document.body
    );
};

export default ActionsDropdown;